"use client";
import React, { Children, isValidElement, useEffect, useRef } from "react";
import { InfoBoxProps } from "../types";
import InfoBox from "..";
import { useInView } from "react-intersection-observer";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { ElementType } from "@/constants/tagular/main";

function isInfoBoxItem(child: React.ReactNode) {
  return (
    isValidElement(child) &&
    (child.type === InfoBox ||
      (typeof child.type === "object" &&
        child.props &&
        typeof child.props === "object" &&
        "shortcodeName" in child.props &&
        (child.props as any).shortcodeName === "info-box-item"))
  );
}

export default function NestedInfoBox({
  heading,
  headingLevel = "p",
  location,
  position = "1",
  children,
}: InfoBoxProps) {
  const infoBoxItems: React.ReactNode[] = [];
  const otherContent: React.ReactNode[] = [];

  Children.forEach(children, (child) => {
    if (isInfoBoxItem(child)) {
      infoBoxItems.push(child);
    } else if (child) {
      otherContent.push(child);
    }
  });

  const webElement: ElementViewed["webElement"] = {
    elementType: ElementType.Component,
    htmlId: "nested-info-box",
    location: location || "nested-info-box",
    name: heading ? heading.replace(/\s+/g, "-").toLowerCase() : "info-box",
    text: heading,
    position: position,
  };

  const { elementViewed } = useElementEvents({ webElement });
  const { inView, ref } = useInView();
  const viewedRef = useRef(false);

  useEffect(() => {
    if (inView && !viewedRef.current) {
      elementViewed();
      viewedRef.current = true;
    }

    if (!inView) {
      viewedRef.current = false;
    }
  }, [inView, elementViewed]);

  return (
    <div ref={ref} className="container">
      {heading && (
        <div className="lg:text-left my-6">
          {React.createElement(
            headingLevel,
            { className: "text-primary-500 font-bold !my-0" },
            heading,
          )}
        </div>
      )}
      <div className="w-full flex flex-col lg:flex-row">
        <div className="lg:w-1/2 md:pr-6">
          <div className="!mb-0">{otherContent}</div>
        </div>
        <div className="flex lg:w-1/2 flex-col items-center">
          {infoBoxItems}
        </div>
      </div>
    </div>
  );
}
